/**
 * sealed-vinyl "신청하기" → 결제 화면 진입 전 선제 검증 훅.
 *
 * 신호 두 가지로 재검증:
 *  - GET /product-orders/availability  → 결제 완료 활성 주문 없음
 *  - GET /product-orders/item-check    → 봉투 판매여부(isOnSale) + 남은 재고(stock)
 *
 * 결과 kind 구분:
 *  - soldOut       : 판매중지(isOnSale=false) 또는 재고 0 — 신청 자체 불가
 *  - notEnoughStock: 재고는 있으나 남은 수량 < 선택 수량 — 수량 줄이면 가능 (화면 유지)
 *  - failed        : 조회 실패
 * availability=false 는 "소진 화면 재사용" 정책에 따라 soldOut 으로 묶는다.
 * detail API 는 분기 신호로 사용하지 않음 (수거 이력 기반 판단 금지 정책).
 */

import { useState } from 'react'

import { getUserFacingMessage } from '@/lib/errorMessage'
import { apiGetSealedItemStock, apiGetSealedOrderAvailability } from '@/payment/api'

/**
 * 신청·결제·수량변경 단계에서 재고 문제로 차단할 때의 안내 모달 종류.
 * 둘 다 확인 시 현재 화면 유지 (이동 없음).
 *  - soldOut        : 판매중지·재고 0 — 신청 자체 불가
 *  - notEnoughStock : 재고는 있으나 남은 수량 < 선택 수량 — 수량 줄이면 가능
 */
export type BlockDialogKind = 'soldOut' | 'notEnoughStock'

export type ProceedToPaymentResult =
  | { ok: true }
  | { ok: false; kind: 'soldOut' }
  | { ok: false; kind: 'notEnoughStock' }
  | { ok: false; kind: 'failed'; message: string }

export function useProceedToPayment() {
  const [isChecking, setIsChecking] = useState(false)

  /** @param quantity 신청 수량 — 남은 재고(stock)와 비교 */
  const proceed = async (quantity: number): Promise<ProceedToPaymentResult> => {
    setIsChecking(true)
    try {
      const [isAvailable, itemStock] = await Promise.all([
        apiGetSealedOrderAvailability(),
        apiGetSealedItemStock(),
      ])
      // 판매중지·재고 0·활성주문 → 신청 자체 불가(soldOut)
      if (!isAvailable || !itemStock.isOnSale || itemStock.stock <= 0) {
        return { ok: false, kind: 'soldOut' }
      }
      // 재고는 있으나 선택 수량보다 적음 → 수량 줄이면 가능(notEnoughStock, 화면 유지)
      if (itemStock.stock < quantity) {
        return { ok: false, kind: 'notEnoughStock' }
      }
      return { ok: true }
    } catch (error) {
      return { ok: false, kind: 'failed', message: getUserFacingMessage(error) }
    } finally {
      setIsChecking(false)
    }
  }

  return { isChecking, proceed }
}
